import type { Connector } from '../shapes/Connector';
import type { ArrowHead, Point } from '../state/types';
import { COLORS } from '../../../config/constants';
import { getConnectorLabelLayout, getConnectorLabelTheme } from './ConnectorLabelLayout';
import { getViewportScale } from '../shapes/selectionRender';
import {
  getConnectorEndArrowPoints,
  getConnectorPoints,
  getConnectorStartArrowPoints,
} from './ConnectorGeometry';

export const renderConnector = (
  ctx: CanvasRenderingContext2D,
  connector: Connector,
  inverted = false
): void => {
  const points = getConnectorPoints({
    startPoint: connector.startPoint,
    endPoint: connector.endPoint,
    waypoints: connector.waypoints,
    arcPoint: connector.arcPoint,
  });
  if (points.length < 2) return;

  ctx.save();

  ctx.strokeStyle = connector.style.stroke;
  ctx.lineWidth = connector.style.strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  switch (connector.style.lineStyle) {
    case 'dashed':
      ctx.setLineDash([8, 4]);
      break;
    case 'dotted':
      ctx.setLineDash([2, 4]);
      break;
    default:
      ctx.setLineDash([]);
  }

  ctx.beginPath();
  ctx.moveTo(points[0]!.x, points[0]!.y);

  if (connector.arcPoint && connector.waypoints.length === 0) {
    ctx.quadraticCurveTo(
      connector.arcPoint.x,
      connector.arcPoint.y,
      connector.endPoint.x,
      connector.endPoint.y
    );
  } else {
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i]!.x, points[i]!.y);
    }
  }
  ctx.stroke();

  ctx.setLineDash([]);

  const endArrow = getConnectorEndArrowPoints({
    startPoint: connector.startPoint,
    endPoint: connector.endPoint,
    waypoints: connector.waypoints,
    arcPoint: connector.arcPoint,
  });
  renderArrowHead(ctx, endArrow.from, endArrow.to, connector.style.endArrow, connector.style);

  if (connector.style.startArrow !== 'none') {
    const startArrow = getConnectorStartArrowPoints({
      startPoint: connector.startPoint,
      endPoint: connector.endPoint,
      waypoints: connector.waypoints,
      arcPoint: connector.arcPoint,
    });
    renderArrowHead(ctx, startArrow.from, startArrow.to, connector.style.startArrow, connector.style);
  }

  ctx.restore();

  if (connector.text) {
    renderConnectorText(ctx, connector, inverted);
  }

  if (connector.selected) {
    renderConnectorSelection(ctx, connector);
  }
};

const renderArrowHead = (
  ctx: CanvasRenderingContext2D,
  from: Point,
  to: Point,
  type: ArrowHead,
  style: Connector['style']
): void => {
  if (type === 'none') return;

  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const size = 12;

  ctx.save();
  ctx.translate(to.x, to.y);
  ctx.rotate(angle);

  ctx.fillStyle = style.stroke;
  ctx.strokeStyle = style.stroke;
  ctx.lineWidth = style.strokeWidth;

  switch (type) {
    case 'arrow':
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-size, -size / 2);
      ctx.lineTo(-size, size / 2);
      ctx.closePath();
      ctx.fill();
      break;

    case 'diamond':
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-size / 2, -size / 3);
      ctx.lineTo(-size, 0);
      ctx.lineTo(-size / 2, size / 3);
      ctx.closePath();
      ctx.fill();
      break;

    case 'circle':
      ctx.beginPath();
      ctx.arc(-size / 2, 0, size / 3, 0, Math.PI * 2);
      ctx.fill();
      break;
  }

  ctx.restore();
};

const renderConnectorText = (
  ctx: CanvasRenderingContext2D,
  connector: Connector,
  inverted: boolean
): void => {
  const layout = getConnectorLabelLayout(ctx, connector);
  const theme = getConnectorLabelTheme(inverted);

  ctx.save();
  ctx.font = layout.font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.fillStyle = theme.bgColor;
  ctx.fillRect(layout.rect.x, layout.rect.y, layout.rect.width, layout.rect.height);

  ctx.fillStyle = theme.textColor;
  ctx.fillText(connector.text, layout.textX, layout.textY);
  ctx.restore();
};

export const renderConnectorSelection = (
  ctx: CanvasRenderingContext2D,
  connector: Connector
): void => {
  const scale = getViewportScale(ctx);
  const handleSize = 8 / scale;
  ctx.fillStyle = COLORS.white;
  ctx.strokeStyle = COLORS.accent;
  ctx.lineWidth = 2 / scale;

  ctx.beginPath();
  ctx.arc(connector.startPoint.x, connector.startPoint.y, handleSize / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(connector.endPoint.x, connector.endPoint.y, handleSize / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  if (connector.arcPoint) {
    ctx.fillStyle = COLORS.selectionArcFill;
    ctx.strokeStyle = COLORS.selectionArcStroke;
    ctx.beginPath();
    ctx.moveTo(connector.arcPoint.x, connector.arcPoint.y - handleSize / 2);
    ctx.lineTo(connector.arcPoint.x + handleSize / 2, connector.arcPoint.y);
    ctx.lineTo(connector.arcPoint.x, connector.arcPoint.y + handleSize / 2);
    ctx.lineTo(connector.arcPoint.x - handleSize / 2, connector.arcPoint.y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = COLORS.white;
    ctx.strokeStyle = COLORS.accent;
  }

  for (const wp of connector.waypoints) {
    ctx.beginPath();
    ctx.rect(wp.x - handleSize / 2, wp.y - handleSize / 2, handleSize, handleSize);
    ctx.fill();
    ctx.stroke();
  }
};
